import { useEffect, useRef, useState } from "react";
import { useSnapshot } from "valtio";
import TWButton from "../../../../components/TWButton";
import CalendarBlankThinIcon from "../../../../components/icons/CalendarBlankThinIcon";
import MagnifyingGlassThinIcon from "../../../../components/icons/MagnifyingGlassThinIcon";
import { useInfiniteGuestListQuery } from "../../../../apis/guest";
import { Guest } from "../../../../types/guest";
import { formatToCustomDisplay } from "../../../../utils/dateUtils";
import { editorState } from "../../../../utils/editor";
import DateRangeDropdown, { DateRange } from "./DateRangeDropdown";
import GuestFilterDropdown, { GuestFilters } from "./GuestFilterDropdown";
import {
  MAX_ACCESS_CARD_GENERATION_BATCH_SIZE,
  toggleLoadedGuestSelection,
} from "../selection";

interface GuestSelectionProps {
  eventId: string;
  selectedGuests: Set<string>;
  onGuestsSelected: (guestIds: Set<string>) => void;
  onBack: () => void;
}

const GuestSelection = ({
  eventId,
  selectedGuests,
  onGuestsSelected,
  onBack,
}: GuestSelectionProps) => {
  const [selectedGuestsList, setSelectedGuestsList] = useState<Set<string>>(
    new Set(selectedGuests)
  );
  const [searchTerm, setSearchTerm] = useState("");
  const [guestFilters, setGuestFilters] = useState<GuestFilters>({});
  const [dateRange, setDateRange] = useState<DateRange>({});
  const [isDateDropdownOpen, setIsDateDropdownOpen] = useState(false);
  const dateButtonRef = useRef<HTMLButtonElement>(null);
  const observerRef = useRef<HTMLDivElement>(null);
  const { eventSchedules } = useSnapshot(editorState);

  // Use TanStack Query for guest list
  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
  } = useInfiniteGuestListQuery({
    eventId,
    search: searchTerm || undefined,
    // Only pass defined filter values
    ...(guestFilters.rsvpComingFor && { rsvpComingFor: guestFilters.rsvpComingFor }),
    ...(guestFilters.relationship && { relationship: guestFilters.relationship }),
    ...(guestFilters.inviteStatus && { inviteStatus: guestFilters.inviteStatus }),
    ...(dateRange.from && { from: dateRange.from }),
    ...(dateRange.to && { to: dateRange.to }),
  });

  // Extract guest data from infinite query
  const guestList = data?.pages.flatMap((page) => page.guestList) ?? [];
  const guestCount = data?.pages[0]?.guestCount ?? 0;
  const hasMore = hasNextPage;

  // Intersection observer for infinite scroll
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !isFetchingNextPage) {
          fetchNextPage();
        }
      },
      { threshold: 0.1 }
    );

    if (observerRef.current) {
      observer.observe(observerRef.current);
    }

    return () => observer.disconnect();
  }, [fetchNextPage, hasMore, isFetchingNextPage]);

  const handleGuestToggle = (guestId: string) => {
    setSelectedGuestsList((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(guestId)) {
        newSet.delete(guestId);
      } else {
        newSet.add(guestId);
      }
      return newSet;
    });
  };

  const loadedGuestIds = guestList.map((guest) => guest._id);
  const allLoadedGuestsSelected =
    loadedGuestIds.length > 0 &&
    loadedGuestIds.every((guestId) => selectedGuestsList.has(guestId));

  const handleSelectLoaded = () => {
    setSelectedGuestsList((previous) =>
      toggleLoadedGuestSelection(previous, loadedGuestIds),
    );
  };

  const handleNext = () => {
    onGuestsSelected(selectedGuestsList);
  };


  const handleApplyDateRange = (range: DateRange) => {
    setDateRange(range);
  };

  // Count active filters
  const hasDateFilter = dateRange.from || dateRange.to;

  const eventMap = eventSchedules.reduce(
    (acc, event) => ({ ...acc, [event._id]: event.eventType.toUpperCase() }),
    {} as Record<string, string>
  );

  // Get button class names based on guest state
  const getButtonClassName = (guest: Guest) => {
    const baseClasses =
      "w-8 h-8 rounded-full border-2 flex items-center justify-center";

    if (selectedGuestsList.has(guest._id)) {
      return `${baseClasses} bg-black border-black text-white`;
    }

    return `${baseClasses} border-gray-300 text-gray-400 hover:border-gray-400`;
  };

  return (
    <div className="px-4 pb-6 flex flex-col h-[656px]">
      {/* Stage Progress */}
      <div className="flex justify-between mb-8 font-glacial font-normal text-xs leading-[123%] tracking-normal uppercase text-gray-400">
        <span>1. Select Event</span>
        <span className="text-gray-900">2. Guests</span>
        <span>3. Summary</span>
      </div>

      {/* Search Bar */}
      <div className="flex items-end justify-between mb-6 px-8">
        <div className="relative w-80 pr-12">
          <input
            type="text"
            placeholder="Search name"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-3 border-b border-gray-300 focus:border-gray-500 outline-none font-glacial text-sm"
          />
          <MagnifyingGlassThinIcon
            className="absolute left-3 top-3.5 w-4 h-4"
            color="#9CA3AF"
          />
        </div>
        <div className="flex items-center space-x-4 relative">
          {/* Filter Dropdown */}
          <GuestFilterDropdown
            filters={guestFilters}
            onFiltersChange={setGuestFilters}
          />
          
          <button
            ref={dateButtonRef}
            onClick={() => setIsDateDropdownOpen(!isDateDropdownOpen)}
            className="mb-1 relative"
          >
            <CalendarBlankThinIcon className="w-6 h-6" color="#6B7280" />
            {hasDateFilter && (
              <span className="absolute -top-1 -right-1 w-2 h-2 bg-black rounded-full" />
            )}
          </button>
          
          {/* Date Range Dropdown */}
          <DateRangeDropdown
            isOpen={isDateDropdownOpen}
            onClose={() => setIsDateDropdownOpen(false)}
            onApply={handleApplyDateRange}
            initialDateRange={dateRange}
            anchorRef={dateButtonRef}
          />
        </div>
      </div>

      {/* Guest Count and Select All */}
      <div className="flex justify-between items-center mb-6">
        <span className="font-glacial font-semibold text-sm uppercase tracking-wide">
          GUESTS({selectedGuestsList.size}/{guestCount})
        </span>
        <button
          onClick={handleSelectLoaded}
          className="font-glacial text-sm text-gray-600 hover:text-gray-800"
        >
          {allLoadedGuestsSelected ? "Clear loaded" : "Select loaded"}
        </button>
      </div>

      {selectedGuestsList.size > MAX_ACCESS_CARD_GENERATION_BATCH_SIZE && (
        <p className="mb-4 text-xs text-red-600" role="alert">
          Select no more than {MAX_ACCESS_CARD_GENERATION_BATCH_SIZE} guests per
          generation.
        </p>
      )}

      {/* Guest List */}
      <div className="flex-1 space-y-4 overflow-y-auto">
        {/* Initial Loading State */}
        {isLoading && (
          <div className="flex flex-col items-center justify-center h-full">
            <div className="w-8 h-8 border-4 border-gray-300 border-t-gray-600 rounded-full animate-spin"></div>
            <span className="mt-4 font-glacial text-sm text-gray-500">
              Loading guests...
            </span>
          </div>
        )}

        {/* Guest List */}
        {!isLoading && guestList.map((guest) => (
          <div key={guest._id} className="flex items-start justify-between">
            <div className="flex items-start space-x-3">
              <div className="w-10 h-10 bg-black rounded-full flex items-center justify-center text-white font-semibold">
                {guest.firstname?.charAt(0)?.toUpperCase() || "G"}
              </div>
              <div>
                <h3 className="font-glacial font-medium text-base">
                  {guest.firstname} {guest.lastname}
                </h3>
                <div className="flex flex-wrap gap-2 mt-1 max-w-[264px]">
                  <span className="px-2 py-0.5 bg-gray-100 text-gray-600 text-[10px] font-glacial rounded-full whitespace-nowrap">
                    {guest.rsvpRelationship.length < 4
                      ? "GUEST"
                      : guest.rsvpRelationship.toUpperCase()}
                  </span>
                  {guest.events.map((guestEventId) => (
                    <span
                      key={`${guest._id}-${guestEventId}`}
                      className="px-2 py-0.5 bg-gray-100 text-gray-600 text-[10px] font-glacial rounded-full whitespace-nowrap"
                    >
                      {eventMap[guestEventId]}
                    </span>
                  ))}
                  <span className="px-2 py-0.5 bg-gray-100 text-gray-600 text-[10px] font-glacial rounded-full whitespace-nowrap">
                    {guest.inviteStatus?.toUpperCase()}
                  </span>
                </div>
                <p className="text-xs text-gray-500 font-glacial mt-1">
                  Added {formatToCustomDisplay(guest.createdAt)}
                </p>
              </div>
            </div>
            <button
              onClick={() => handleGuestToggle(guest._id)}
              className={getButtonClassName(guest as Guest)}
            >
              <svg
                className="w-4 h-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                {selectedGuestsList.has(guest._id) ? (
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M5 13l4 4L19 7"
                  />
                ) : (
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 6v6m0 0v6m0-6h6m-6 0H6"
                  />
                )}
              </svg>
            </button>
          </div>
        ))}

        {/* Loading indicator and intersection observer target */}
        {hasMore && (
          <div ref={observerRef} className="flex justify-center py-4">
            {isFetchingNextPage && (
              <div className="flex items-center space-x-2 text-gray-500">
                <div className="w-4 h-4 border-2 border-gray-300 border-t-gray-600 rounded-full animate-spin"></div>
                <span className="font-glacial text-sm">
                  Loading more guests...
                </span>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Action Buttons */}
      <div className="mt-6 flex justify-around">
        <TWButton onClick={onBack} variant="secondary" className="w-32 h-10">
          BACK
        </TWButton>
        <TWButton
          onClick={handleNext}
          variant="primary"
          className="w-32 h-10"
          disabled={
            selectedGuestsList.size === 0 ||
            selectedGuestsList.size > MAX_ACCESS_CARD_GENERATION_BATCH_SIZE
          }
        >
          NEXT
        </TWButton>
      </div>
    </div>
  );
};

export default GuestSelection;
